/**
 * MetricDetail - per-metric deep-dive modal.
 *
 * Click any tile -> open this modal showing only THAT metric's full data:
 *   - Hero stat with vs-yesterday/week/month/year comparisons
 *   - 12-month trend chart
 *   - Mix donut (where the value comes from)
 *   - Top contributors leaderboard
 *   - Source provenance (DB tables + query summary)
 *   - "Explore in {Section} ->" link to the parent page
 *
 * In Phase F the data backing the modal becomes a real fetch.
 */
import React, { useRef, useEffect } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useFocusTrap } from "../../utils/useFocusTrap";
import { AreaChart } from "./AreaChart.v2";
import { Donut } from "./Donut.v2";
import { Leaderboard } from "./Leaderboard.v2";
import { METRIC_DETAILS, formatINR_, formatNum_ } from "./metricDetails.v2";
import { showProvenance } from "./env.v2";
import { rangeWordFor } from "./rangeWord.v2";
import { useInvestorMetricDetail } from "./useInvestorMetricDetail.v2";

// Reads as a sentence: "Rs 3.10L → Rs 3.42L" with the delta as the verdict.
// Hero already shows today's value, so each pill leads with the reference and
// shows the journey to today, not the repeated current value.
function ComparePill({ label, thisValue, refValue, delta, direction }) {
  const fg =
    direction === "up"   ? T.status.good :
    direction === "down" ? T.status.bad  :
    T.text.muted;
  const bgTint =
    direction === "up"   ? "rgba(76,175,80,0.12)" :
    direction === "down" ? "rgba(255,71,87,0.12)" :
    "rgba(31,42,63,0.06)";
  const sign = direction === "up" ? "+" : direction === "down" ? "-" : "";
  const isNA = delta === "n/a" || refValue === "—" || refValue === "0";
  return (
    <div style={{
      padding: "12px 14px",
      backgroundColor: T.surface.canvas,
      border: "1px solid " + T.border.subtle,
      borderRadius: 10,
      fontFamily: T.font.sans,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: T.text.muted, lineHeight: 1.2, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>

      {isNA ? (
        <>
          <span style={{ fontSize: 18, fontWeight: 800, color: T.text.muted, fontStyle: "italic", letterSpacing: "-0.01em" }}>
            No prior data
          </span>
          <span style={{
            display: "inline-flex", alignSelf: "flex-start",
            fontSize: 11, fontWeight: 700, color: T.text.muted,
            background: "rgba(31,42,63,0.06)",
            padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap",
          }}>
            First time
          </span>
        </>
      ) : (
        <>
          <span style={{ fontSize: 20, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", whiteSpace: "nowrap" }}>
            {refValue}
          </span>
          <span style={{
            display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 4,
            fontSize: 12, fontWeight: 800, color: fg,
            background: bgTint,
            padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}>
            <span style={{ fontSize: 10 }} aria-hidden>{direction === "up" ? "▲" : direction === "down" ? "▼" : "•"}</span>
            {sign}{delta}
          </span>
        </>
      )}
    </div>
  );
}

function BreakdownBlock({ items, color }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      backgroundColor: T.surface.card,
      border: "1px solid " + T.border.subtle, borderLeft: "4px solid " + color,
      borderRadius: 12, padding: 16, marginBottom: 14,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, marginBottom: 12 }}>Breakdown today</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((b, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0, alignSelf: "center" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text.primary, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{b.value}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.text.muted, fontVariantNumeric: "tabular-nums", minWidth: 48, textAlign: "right" }}>{b.pct.toFixed(1)}%</span>
            </div>
            {/* Proportional bar showing share of total */}
            <div style={{ marginLeft: 18, height: 5, borderRadius: 3, backgroundColor: "rgba(31,42,63,0.06)", overflow: "hidden" }}>
              <div style={{ width: Math.max(2, b.pct) + "%", height: "100%", backgroundColor: color, borderRadius: 3, transition: "width 320ms ease" }} />
            </div>
            {b.sub && (
              <div style={{ marginLeft: 18, fontSize: 11, color: T.text.muted, lineHeight: 1.4 }}>{b.sub}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionTitle({ children, hint }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "0 4px", marginBottom: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: T.text.primary }}>{children}</span>
      {hint && <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{hint}</span>}
    </div>
  );
}

export function MetricDetail({ metricId, live, rangeId, range, onClose, onNavigate }) {
  const m = METRIC_DETAILS[metricId];
  // Live drill-in fetch — trend / mix / contributors per metric. Hook
  // short-circuits for unsupported metrics (wallets/home tiles → null).
  const { data: detail, loading: detailLoading } = useInvestorMetricDetail(metricId, range, rangeId);

  // Modal a11y: focus-trap + Escape-to-close + body scroll-lock while open.
  const modalRef = useRef(null);
  useFocusTrap(modalRef, { active: !!m, onEscape: onClose });
  useEffect(() => {
    if (!m) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [m]);

  if (!m) return null;
  const word = rangeWordFor(rangeId);
  // Wallet/snapshot metrics don't react to the pill — they always read "current".
  // Everything else inherits the pill word.
  const SNAPSHOT_METRICS = new Set(["wallet-fans", "wallet-creators", "wallet-brands", "net-float"]);
  const isSnapshot = SNAPSHOT_METRICS.has(metricId);
  const windowLabel = isSnapshot ? "current" : word;
  // Pick a number formatter for the trend line based on the metric class.
  // Money metrics render as Rs/Cr/L; counts render as comma-separated ints.
  const MONEY_METRICS = new Set(["gmv-lifetime", "creator-payouts-lifetime", "refunds-lifetime", "revenue-today"]);
  const fmt = MONEY_METRICS.has(metricId) ? formatINR_ : formatNum_;
  const moneyMetric = MONEY_METRICS.has(metricId);

  return (
    <div onClick={onClose} role="presentation" style={{
      position: "fixed", inset: 0, zIndex: 100,
      backgroundColor: "rgba(31,42,63,0.50)",
      display: "flex", alignItems: "stretch", justifyContent: "center",
      padding: 24,
    }}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={m.title}
        style={{
        flex: 1, maxWidth: 1100,
        backgroundColor: T.surface.canvas,
        borderRadius: 16,
        boxShadow: "0 20px 64px rgba(31,42,63,0.30)",
        display: "flex", flexDirection: "column",
        fontFamily: T.font.sans,
        overflow: "hidden",
      }}>
        {/* Header */}
        <header style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 22px",
          borderBottom: "1px solid " + T.border.subtle,
          backgroundColor: T.surface.card,
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: m.accent + "22", color: m.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 14,
          }}>{m.title.charAt(0)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Metric detail · {windowLabel}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>{m.title}</div>
          </div>
          <button type="button" onClick={() => { onClose(); onNavigate(m.parentPage); }} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 12px",
            background: T.surface.canvas, border: "1px solid " + T.border.subtle,
            borderRadius: 10,
            color: T.text.primary, fontSize: 12, fontWeight: 700,
            cursor: "pointer", outline: "none", fontFamily: T.font.sans,
          }}>Explore in {m.parentLabel} →</button>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: 36, height: 36, borderRadius: 10,
            backgroundColor: T.surface.canvas, border: "1px solid " + T.border.subtle,
            color: T.text.secondary, fontSize: 16,
            cursor: "pointer", outline: "none",
          }}>✕</button>
        </header>

        {/* Body */}
        <div style={{ flex: 1, padding: 22, overflowY: "auto" }}>
          {/* Live-hero block — rendered when the parent passes live tile data
              (the 2026-04-28 mock-strip removed the canned `m.hero` blocks; we
              render the real value the user just clicked instead, plus the
              source provenance below). Honest representation of what we have:
              one number + its sub-label, no fabricated comparison pills. */}
          {!m.hero && live && (
            <div style={{
              padding: 18, marginBottom: 14,
              backgroundColor: T.surface.card,
              border: "1px solid " + T.border.subtle, borderLeft: "4px solid " + m.accent,
              borderRadius: 12,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.text.muted }}>
                {live.label || m.title}
              </span>
              <span style={{ fontSize: 36, fontWeight: 800, color: T.text.primary, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                {live.value || "—"}
              </span>
              {live.subValue && (
                <span style={{ fontSize: 13, fontWeight: 500, color: T.text.muted, lineHeight: 1.4 }}>{live.subValue}</span>
              )}
              {live.delta && (
                <span style={{
                  display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 6,
                  fontSize: 12, fontWeight: 700, marginTop: 4,
                  color: live.delta.direction === "up" ? T.status.good : live.delta.direction === "down" ? T.status.bad : T.text.muted,
                  background: live.delta.direction === "up" ? "rgba(76,175,80,0.12)" : live.delta.direction === "down" ? "rgba(255,71,87,0.12)" : "rgba(31,42,63,0.06)",
                  padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{ fontSize: 10 }} aria-hidden>{live.delta.direction === "up" ? "▲" : live.delta.direction === "down" ? "▼" : "•"}</span>
                  {live.delta.direction === "up" ? "+" : live.delta.direction === "down" ? "-" : ""}{live.delta.value}
                </span>
              )}
            </div>
          )}

          {/* Canned-mock hero — only renders if metric metadata still has a
              fabricated hero block. The 2026-04-28 strip removed all of these,
              so this branch effectively never fires today; kept as a no-op
              fallback in case a future metric ships its own static hero. */}
          {m.hero && (
            <div style={{
              padding: 18, marginBottom: 14,
              backgroundColor: T.surface.card,
              border: "1px solid " + T.border.subtle, borderLeft: "4px solid " + m.accent,
              borderRadius: 12,
              display: "flex", flexDirection: "column", gap: 14,
            }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 14 }}>
                <span style={{ fontSize: 36, fontWeight: 800, color: T.text.primary, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                  {m.hero.value}
                </span>
                {m.hero.delta && (() => {
                  const dir = m.hero.delta.direction;
                  const sign = dir === "up" ? "+" : dir === "down" ? "-" : "";
                  const fg = dir === "up" ? T.status.good : dir === "down" ? T.status.bad : T.text.muted;
                  return (
                    <span style={{ fontSize: 13, fontWeight: 700, color: fg, fontVariantNumeric: "tabular-nums" }}>
                      {sign}{m.hero.delta.value}
                    </span>
                  );
                })()}
                <span style={{ fontSize: 12, fontWeight: 500, color: T.text.muted }}>{m.hero.delta && m.hero.delta.vsLabel}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted }}>{m.hero.asOf}</span>
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
                gap: 10,
              }}>
                {(m.hero.compares || []).map((c, i) => (
                  <ComparePill key={i}
                    label={c.label}
                    thisValue={c.thisValue}
                    refValue={c.refValue}
                    delta={c.delta}
                    direction={c.direction}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Breakdown — same labels the tile shows, expanded with bars + context */}
          {m.breakdown && m.breakdown.length > 0 && (
            <BreakdownBlock items={m.breakdown} color={m.accent} />
          )}

          {/* Stage 2c — live trend + mix + contributors from
              /api/v1/investor/metric/:id. Each section renders only when
              the backend returned points for it; loading skeleton fills the
              gap so the modal isn't empty during fetch. */}
          {detailLoading && (
            <div style={{
              padding: 18, marginBottom: 14, fontSize: 12, color: T.text.muted,
              backgroundColor: T.surface.card, border: "1px solid " + T.border.subtle,
              borderRadius: 12, textAlign: "center",
            }}>Loading trend, mix and top contributors…</div>
          )}

          {detail?.trend?.points?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <SectionTitle hint={(detail.trend.granularity || '').toUpperCase()}>Trend · {windowLabel}</SectionTitle>
              <AreaChart
                data={detail.trend.points}
                format={fmt}
                summaryLabel={detail.trend.points[detail.trend.points.length - 1]?.label || "Latest"}
                summaryValue={live?.value}
                summaryDelta=""
              />
            </div>
          )}

          {/* Mix + contributors — live data from Stage 2c */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
            gap: 14, marginBottom: 14, alignItems: "stretch",
          }}>
            {detail?.mix?.segments?.length > 0 && (
              <Donut
                title="Mix"
                subtitle={detail.mix.subtitle}
                data={detail.mix.segments}
                format={detail.mix.format === "money" ? formatINR_ : formatNum_}
                centerLabel={detail.mix.center?.label || "Total"}
                centerValue={(detail.mix.format === "money" ? formatINR_ : formatNum_)(detail.mix.center?.value || 0)}
              />
            )}
            {detail?.contributors?.rows?.length > 0 && (
              <Leaderboard
                title={detail.contributors.title}
                subtitle={`Top performers · ${windowLabel}`}
                rows={detail.contributors.rows}
                rightLabel={detail.contributors.rightLabel || "Value"}
                format={detail.contributors.format === "money" ? formatINR_ : formatNum_}
              />
            )}
          </div>

          {/* Stage-2c-not-yet-wired note — shown only when no live drill-in
              came back from the backend AND the metric metadata has no static
              content. For supported metrics (gmv / payouts / brand-deals /
              refunds / virality) the live trend renders above and this block
              stays hidden. */}
          {!detailLoading && !detail?.trend && !detail?.mix && !detail?.contributors
            && !m.breakdown && !m.trend && !m.mix && !m.contributors && (
            <div style={{
              padding: 18, marginBottom: 14,
              backgroundColor: T.surface.card,
              border: "1px dashed " + T.border.default,
              borderLeft: "4px solid " + m.accent,
              borderRadius: 12,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, marginBottom: 8 }}>
                Deep-dive in progress
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, marginBottom: 6 }}>
                Live trend, mix, and top-contributors for {m.title} are being wired to Postgres.
              </div>
              <div style={{ fontSize: 12, color: T.text.muted, lineHeight: 1.5 }}>
                The tile value on the previous screen is real and live. The deeper drill-in (12-month trend, concentration mix, top-N contributors leaderboard) requires new SQL queries — tracked as <strong>Stage 2c</strong>. Until that lands, the modal shows only the verifiable summary + the source provenance below; no fabricated numbers.
              </div>
            </div>
          )}

          {/* Source provenance — STRICT: hidden in production. Never leaks DB schema to live. */}
          {m.source && showProvenance() && (
            <div style={{
              padding: 14,
              backgroundColor: T.surface.card,
              border: "1px solid " + T.border.subtle, borderLeft: "4px solid #7C4DFF",
              borderRadius: 12, marginBottom: 14,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text.primary, marginBottom: 8 }}>Where this data comes from</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                {(m.source.tables || []).map((t) => (
                  <span key={t} style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                    color: T.text.secondary, background: T.surface.canvas,
                    border: "1px solid " + T.border.subtle,
                    padding: "3px 8px", borderRadius: 999,
                    fontFamily: T.font.mono,
                  }}>{t}</span>
                ))}
                {m.source.rows != null && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, padding: "3px 4px" }}>
                    {m.source.rows.toLocaleString("en-IN")} rows · refreshed {m.source.refreshed}
                  </span>
                )}
              </div>
              {m.source.query && (
                <div style={{ fontSize: 11, color: T.text.muted, fontFamily: T.font.mono, lineHeight: 1.4 }}>
                  {m.source.query}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 10, padding: "14px 22px",
          borderTop: "1px solid " + T.border.subtle,
          backgroundColor: T.surface.card,
        }}>
          <button type="button" onClick={onClose} style={{
            padding: "9px 14px",
            background: T.surface.canvas, border: "1px solid " + T.border.subtle,
            borderRadius: 10,
            color: T.text.primary, fontSize: 13, fontWeight: 600,
            cursor: "pointer", outline: "none", fontFamily: T.font.sans,
          }}>Close</button>
          {/*
            "Build a report on this" CTA removed pre-launch — it opened
            ReportBuilder which still renders mock leaderboard previews
            (Aarav Sharma / Tata CLiQ / etc.). Re-enable once ReportBuilder
            is wired to live data via the same hooks the page tiles use.
          */}
          <button type="button"
            onClick={() => { onClose(); onNavigate(m.parentPage); }}
            style={{
              padding: "9px 16px",
              background: m.accent, border: "1px solid " + m.accent,
              borderRadius: 10,
              color: "#FFFFFF", fontSize: 13, fontWeight: 700,
              cursor: "pointer", outline: "none", fontFamily: T.font.sans,
            }}>Open {m.parentLabel || "section"} →</button>
        </footer>
      </div>
    </div>
  );
}
